<template>
  <div class="px-4 sm:px-6 lg:px-8 py-6 bg-white min-h-screen" v-if="canView">
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Employee Schedule -->
      <div
        class="rounded-2xl p-6 shadow-[4px_4px_8px_rgba(0,0,0,0.1)] lg:col-span-2"
        style="background: linear-gradient(to bottom right, #ffffff, #f2f2f2);"
      >
        <div class="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div>
            <h1 class="text-[26px] md:text-[30px] font-extrabold text-gray-900">Employee Schedule</h1>
            <p class="text-[18px] md:text-[20px] text-center font-extrabold text-black mt-4 md:mt-5 mb-4">
              {{ formattedHeaderDate }}
            </p>
          </div>
          <div class="flex gap-6 pt-2 md:mx-20 text-[22px] md:text-[25px] justify-center">
            <span class="font-extrabold text-black border-b-2 border-black pb-1">Daily</span>
            <span class="text-gray-400 font-semibold">Weekly</span>
            <span class="text-gray-400 font-semibold">Monthly</span>
          </div>
        </div>
        <div class="flex flex-col sm:flex-row">
          <div class="hidden sm:flex flex-col justify-between h-56 mr-6 text-sm text-black-400 mt-10">
            <span>60</span>
            <span>50</span>
            <span>40</span>
            <span>30</span>
            <span>20</span>
            <span>10</span>
          </div>
          <div class="flex justify-between gap-4 w-full sm:ml-8 h-56 mt-10">
            <div
              v-for="(day, idx) in days"
              :key="idx"
              class="flex flex-col items-center w-full"
            >
              <div :class="['w-8 sm:w-10', 'rounded-md', day.active ? 'bg-cyan-500 h-[95%]' : 'bg-gray-300 h-[80%]']"></div>
              <div class="text-sm sm:text-base mt-2 text-gray-600">{{ day.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column -->
      <div class="flex flex-col gap-6">
        <!-- Attendance Rate -->
        <div
          class="rounded-2xl shadow-[4px_4px_8px_rgba(0,0,0,0.1)] p-6 pt-8"
          style="background: linear-gradient(to bottom right, #ffffff, #f2f2f2);"
        >
          <h2 class="text-[26px] md:text-[30px] font-extrabold text-gray-900 mb-4">Attendance Rate</h2>
          <div class="flex items-center justify-center relative mt-10">
            <svg class="w-28 h-28 md:w-32 md:h-32 transform -rotate-90" viewBox="0 0 36 36">
              <circle
                class="text-gray-200"
                stroke="currentColor"
                stroke-width="5"
                fill="transparent"
                r="16"
                cx="18"
                cy="18"
              />
              <circle
                class="text-blue-600"
                stroke="currentColor"
                stroke-width="4"
                stroke-dasharray="70, 100"
                stroke-linecap="round"
                fill="transparent"
                r="16"
                cx="18"
                cy="18"
              />
            </svg>
            <div class="absolute text-xl md:text-2xl font-bold text-gray-900">70%</div>
          </div>
        </div>

        <!-- Active Users -->
        <div
          class="rounded-2xl shadow-[4px_4px_8px_rgba(0,0,0,0.1)] p-6 flex justify-between items-end"
          style="background: linear-gradient(to bottom right, #ffffff, #f2f2f2);"
        >
          <div>
            <h2 class="text-[26px] md:text-[30px] font-extrabold text-gray-900 mb-2">Active users</h2>
            <p class="text-[26px] md:text-[30px] font-extrabold text-gray-900 mt-6">300k</p>
          </div>
          <div class="flex items-center gap-1 text-green-600 text-[18px] md:text-[20px] font-extrabold mb-2">
            <svg class="w-6 h-6 md:w-8 md:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M3 17L9 11L13 15L21 7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M21 7H17" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            4.5%
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import { loadMyPermissions, hasAny } from '@/utils/permissions'

const router = useRouter()
const canView = ref(false)

const days = [
  { label: 'Sat', active: false },
  { label: 'Sun', active: true },
  { label: 'Mon', active: false },
  { label: 'Tue', active: false },
  { label: 'Wed', active: false },
  { label: 'Thu', active: false },
]

const formattedHeaderDate = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: '2-digit',
  year: 'numeric',
  timeZone: 'Asia/Amman',
})
  .format(new Date())
  .replace(', ', ' , ')
  .replace(', ', ' , ')

onMounted(async () => {
  await loadMyPermissions(false)
  if (hasAny(['dashboard.view'])) {
    canView.value = true
  } else {
    await Swal.fire({
      icon: 'error',
      title: 'No Permission',
      text: 'You are not allowed to view Dashboard.',
      toast: true,
      position: 'top',
      timer: 2200,
      showConfirmButton: false,
    })
    try { router.back() } catch (_) { router.replace('/') }
  }
})
</script>

<style scoped>
svg circle {
  transition: stroke-dasharray 0.5s ease-in-out;
}
</style>
